import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/tidewater.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// PostHog analytics
import posthog from 'posthog-js';
import { initHubSpot } from './analytics/hubspot';
import { PostHogProvider, PostHogErrorBoundary } from '@posthog/react';

// Initialize PostHog. Analytics is routed first-party through our own origin
// (/lanura/ -> nginx -> PostHog EU Cloud) so ad/tracking blockers don't drop
// events. REACT_APP_POSTHOG_HOST can still override (e.g. local dev pointing
// straight at PostHog). ui_host keeps "view in PostHog" deep-links working.
if (process.env.REACT_APP_POSTHOG_KEY) {
  // Consent model (UK PECR / EU ePrivacy): start cookieless (memory
  // persistence, no session recording). If the visitor has accepted the
  // cookie banner (see components/CookieConsent.tsx), upgrade to persistent
  // analytics + session replay. Declining sends nothing at all: PECR wants
  // consent for analytics whether or not they use cookies, and a retest found
  // a declined visitor still posting events on every page load (R20).
  let cookieConsent: string | null = null;
  try {
    cookieConsent = localStorage.getItem('lanura_cookie_consent');
  } catch {}
  const consented = cookieConsent === 'accepted';
  const declined = cookieConsent === 'declined';
  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host:
      process.env.REACT_APP_POSTHOG_HOST ||
      (typeof window !== 'undefined'
        ? `${window.location.origin}/lanura`
        : 'https://eu.i.posthog.com'),
    ui_host: 'https://eu.posthog.com',
    persistence: consented ? 'localStorage+cookie' : 'memory',
    opt_out_capturing_by_default: declined,
    disable_session_recording: !consented,
    person_profiles: 'identified_only' as any,
    // Route changes are pageviews too, or a single-page visit is one
    // $pageview and the session duration PostHog derives from it is empty.
    capture_pageview: 'history_change',
    capture_pageleave: true,
    session_recording: {
      // Marketing pages never need replay, and rrweb's per-mutation style
      // diffing was measurable per animation frame on the landing (framer
      // writes inline transforms each frame). Blocked at the recorder level.
      blockSelector: '.landing-root',
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
      maskTextSelector: undefined,
    },
  });
}

/* HubSpot, after PostHog and on the same consent rule. Live hosts only, and a
 * no-op everywhere else. See analytics/hubspot.ts for why it is created here
 * rather than pasted into index.html.
 *
 * Deferred until the browser is idle, which is a change from calling it
 * inline. One tag here fetches four more (hs-scripts pulls hs-analytics,
 * hs-banner and hscollectedforms, about 97 KiB together) and every one of them
 * executes on the main thread. Run during load, that competes with the work
 * that actually paints the page: PageSpeed put six long tasks after first
 * paint and the landing page's Speed Index at 6.8s.
 *
 * Analytics is not worth a millisecond of someone's first impression, and
 * nothing is lost by waiting: the pageview is sent whenever the loader starts.
 * requestIdleCallback where it exists, a timeout after load elsewhere, and
 * never before load, so it cannot sneak into the critical window.
 *
 * The call stays here rather than inside initHubSpot so that the ordering
 * guarantee it makes, doNotTrack queued before the loader exists, stays
 * synchronous and testable. */
const startHubSpot = () => {
  const w = window as any;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(() => initHubSpot(), { timeout: 5000 });
  } else {
    window.setTimeout(() => initHubSpot(), 2000);
  }
};
if (document.readyState === 'complete') startHubSpot();
else window.addEventListener('load', startHubSpot, { once: true });

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary>
        {/* No GoogleOAuthProvider here on purpose. It injects Google's
            sign-in library the moment it mounts, and at the root that meant
            every marketing visitor paid for a script their page has no use
            for. It now wraps only the three routes that can sign in. See
            components/auth/GoogleSignInScope.tsx. */}
        <App />
      </PostHogErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
